import { useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { toast } from 'sonner';

/**
 * Hook for candidate subscription downgrade scheduling.
 *
 * Mirrors useSubscriptionActions for ForkHire but targets
 * candidate_subscriptions via the cancel-subscription edge function.
 */
export function useCandidateSubscriptionActions() {
  const queryClient = useQueryClient();

  /** Schedule a downgrade for next renewal */
  const scheduleDowngrade = useMutation({
    mutationFn: async ({
      subscriptionId,
      downgradePlanId,
    }: {
      subscriptionId: string;
      downgradePlanId: string;
    }) => {
      const { data, error } = await seekSupabase.functions.invoke('cancel-subscription', {
        body: {
          subscription_id: subscriptionId,
          owner_type: 'candidate',
          action: 'schedule_downgrade',
          downgrade_plan_id: downgradePlanId,
        },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Downgrade scheduling failed');
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Downgrade scheduled for next renewal');
      queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to schedule downgrade');
    },
  });

  /** Cancel a scheduled downgrade */
  const cancelDowngrade = useMutation({
    mutationFn: async (subscriptionId: string) => {
      const { data, error } = await seekSupabase.functions.invoke('cancel-subscription', {
        body: {
          subscription_id: subscriptionId,
          owner_type: 'candidate',
          action: 'cancel_downgrade',
        },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Failed to cancel downgrade');
      return data;
    },
    onSuccess: () => {
      toast.success('Scheduled downgrade cancelled');
      queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to cancel downgrade');
    },
  });

  return { scheduleDowngrade, cancelDowngrade };
}
